import React, { useEffect, useRef, useState, useContext } from 'react';
import { Body, Header, Main } from '../../NecttosComp/Layout/Layout';
import Button from '../../NecttosComp/Button/Button';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import { Table, Tbody, Td, Th, Thead, Tr, useSortableData } from '../../NecttosComp/Table/Table';
import Input2 from '../../NecttosComp/Input/Input2';
import toast from 'react-hot-toast';
import { FirebaseContext } from '../../context/FirebaseContext';

const Toolbar = ({ onCommand }) => {
  return (
    <div
      className='z-50 bg-white border rounded shadow-md p-1 flex gap-1 mb-2'
      style={{ width: '100%', boxSizing: 'border-box', backgroundColor: 'white' }}
    >
      <Button type='refresh' onClick={() => onCommand('bold')}>B</Button>
      <Button type='refresh' onClick={() => onCommand('italic')}>I</Button>
      <Button type='refresh' onClick={() => onCommand('underline')}>U</Button>
      <Button type='refresh' onClick={() => onCommand('strikeThrough')}>S</Button>
      <Button type='refresh' onClick={() => onCommand('fontSize', 5)}>A+</Button>
      <Button type='refresh' onClick={() => onCommand('fontSize', 2)}>A-</Button>
      <Button type='refresh' onClick={() => onCommand('justifyLeft')}>🡸</Button>
      <Button type='refresh' onClick={() => onCommand('justifyCenter')}>↔</Button>
      <Button type='refresh' onClick={() => onCommand('justifyRight')}>🡺</Button>
      <Button type='refresh' onClick={() => onCommand('justifyFull')}>☰</Button>
      <Button type='refresh' onClick={() => onCommand('insertUnorderedList')}>•</Button>
      <Button type='refresh' onClick={() => onCommand('insertOrderedList')}>1.</Button>
      {/* New font color button with color picker */}
      <input
        type="color"
        title="Font Color"
        onChange={(e) => onCommand('foreColor', e.target.value)}
        style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'none', cursor: 'pointer', borderRadius: '4px' }}
      />
      {/* New highlight button with color picker */}
      <input
        type="color"
        title="Highlight"
        onChange={(e) => onCommand('hiliteColor', e.target.value)}
        style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'none', cursor: 'pointer', borderRadius: '4px' }}
      />
    </div>
  );
};

const RichTextEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);

  const handleCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className='relative'>
      <Toolbar onCommand={handleCommand} />
      <div
        ref={editorRef}
        contentEditable
        className='min-h-[300px] border border-gray-300 p-4 rounded bg-white overflow-auto'
        style={{ outline: 'none', minHeight: '50vh', width: '100%' }}
        onInput={handleInput}
        suppressContentEditableWarning={true}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    </div>
  );
};

const Circular = ({ onClose }) => {
  const { userId, role } = useContext(FirebaseContext);
  const ref = useRef(null);

  const [formData, setFormData] = useState({
    refNo: '',
    date: new Date().toISOString().slice(0, 10),
    to: '',
    subject: '',
    body: '',
    cc: '',
    type: 'academic', // default type
  });

  const [circulars, setCirculars] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);

  const fetchCirculars = async () => {
    try {
      const instance = await getAxiosTokenInstance();
      const response = await instance.get('/college/office/circular/getAll');
      setCirculars(response.data.circulars || []);
    } catch (error) {
      console.error('Error fetching circulars:', error);
      toast.error('Failed to fetch circulars');
    }
  };

  useEffect(() => {
    fetchCirculars();
  }, []);

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If type changes, fetch new refNo from backend
    if (name === 'type') {
      fetchRefNo(value);
    }
  };

  // Fetch refNo from backend based on type
  const fetchRefNo = async (type) => {
    try {
      const instance = await getAxiosTokenInstance();
      // Call backend to get new refNo for the selected type
      const response = await instance.post('/college/office/circular/generateRefNo', { type });
      if (response.data && response.data.refNo) {
        setFormData((prev) => ({
          ...prev,
          refNo: response.data.refNo,
        }));
      }
    } catch (error) {
      console.error('Error fetching refNo:', error);
      toast.error('Failed to fetch reference number');
    }
  };

  // Fetch initial refNo on form show
  React.useEffect(() => {
    if (showForm) {
      fetchRefNo(formData.type);
    }
  }, [showForm]);

  const handleBodyChange = (html) => {
    setFormData((prev) => ({
      ...prev,
      body: html,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const instance = await getAxiosTokenInstance();
      const response = await instance.post('/college/office/circular/submit', formData);
      if (response.data?.message?.includes('success')) {
        toast.success('Circular submitted successfully!');
        setFormData({
          refNo: '',
          date: new Date().toISOString().slice(0, 10),
          to: '',
          subject: '',
          body: '',
        });
        setShowForm(false);
        fetchCirculars();
      } else {
        toast.error('Failed to submit circular');
      }
    } catch (error) {
      console.error('Error submitting circular:', error);
      toast.error('Error submitting circular');
    }
  };

  const filteredCirculars = search
    ? circulars.filter((c) =>
        [c.refNo, c.to, c.subject]
          .join(' ')
          .toLowerCase()
          .includes(search.toLowerCase())
      )
    : circulars;

  const { tableItems, requestSort, getIcon } = useSortableData(filteredCirculars);

  // Circular document preview component based on the image
  const CircularPreview = ({ circular }) => {
    const {
      refNo = 'REG/COL/2025/ACAD/001',
      date = '20 May 2025',
      to = 'All Language Departments of Affiliated Colleges',
      subject = 'Upload of FYUGP Semester 3 MDC Course Details to Necttos',
      body = (
        <>
          <p>Respected Sir/Madam,</p>
          <p>
            As part of the FYUGP Semester 3 academic scheduling, all affiliated language departments are requested to upload the details of the <strong>Multidisciplinary Courses (MDC)</strong> offered by your department to the Necttos Eco System via{' '}
            <a href="https://necttos.com/app" target="_blank" rel="noopener noreferrer">
              https://necttos.com/app
            </a>{' '}
            under the section: FYUGP → Registered Courses.
          </p>
          <p>Kindly ensure the following are uploaded:</p>
          <ul>
            <li>List of MDC papers provided by your department</li>
            <li>Subject allocation details</li>
            <li>Corresponding timetables for each course</li>
          </ul>
          <p>
            For ease of reference, please click on the <strong>"Tutorial"</strong> button available in the portal for step-by-step instructions on completing the upload process.
          </p>
          <p>
            <strong>Submission Deadline:</strong> All entries must be completed on or before <u>25 May 2025, by 5:00 PM</u>.
          </p>
          <p>
            The automated student allotment process will commence from <u>26 May 2025 to 28 May 2025</u>, after which final subject allocations and timetables will be generated automatically within the Necttos Eco System, taking into account student preferences and sport admission considerations.
          </p>
          <p>
            Departments unfamiliar with the platform are encouraged to watch the <strong>instructional video</strong> provided in the portal.
          </p>
          <p>
            We appreciate your prompt attention to this matter and your continued cooperation in maintaining an efficient and standardized academic workflow across institutions.
          </p>
          <p>CC:</p>
          <ol>
            <li>The Principal</li>
            <li>Heads of Departments</li>
            <li>Superintendent</li>
            <li>Director/CEO, Necttos</li>
          </ol>
        </>
      ),
      footer = {
        email: 'contact@necttos.com',
        phone: '+91-9400001733',
        website: 'www.necttos.com',
        cin: 'U72900KL2021OPC067869',
        authorizedSignatory: 'Authorized Signatory\nAcademic Coordinator / Registrar\n(Signature & Seal)',
      },
    } = circular || {};

    return (
      <div className="max-w-[800px] mx-auto p-5 font-sans border border-black bg-white text-black">
        <header className="text-center mb-5">
          <img src="/path-to-logo.png" alt="Necttos Logo" className="h-15 mb-2" />
          <h1 className="m-0 text-2xl font-bold">Necttos (OPC) Private Limited</h1>
          <p className="italic my-1">"Empowering Educators to Focus on Teaching and Research"</p>
          <p className="text-xs my-1">
            Necttos Office, 12/306/A, 1st Floor, Ongallur, Pattambi Road, Palakkad District, Kerala - 679313
          </p>
        </header>

        <div className="flex justify-between mb-2 text-sm">
          <div><strong>Ref. No.</strong> {refNo}</div>
          <div><strong>Date:</strong> {date}</div>
        </div>

        <h2 className="text-center underline mb-5">CIRCULAR</h2>

        <p><strong>To:</strong> {to}</p>
        <p><strong>Subject:</strong> {subject}</p>

        <div className="mt-5 text-sm leading-relaxed">
          {body}
        </div>

        <footer className="mt-10 text-xs text-gray-700 border-t border-black pt-2">
          <div>CC:</div>
          <ol className="list-decimal list-inside">
            <li>The Principal</li>
            <li>Heads of Departments</li>
            <li>Superintendent</li>
            <li>Director/CEO, Necttos</li>
          </ol>

          <div className="mt-5 whitespace-pre-line">{footer.authorizedSignatory}</div>

          <div className="mt-5 text-[10px] border-t border-gray-300 pt-2 flex justify-between">
            <div>📞 +91-9400001733</div>
            <div>✉️ contact@necttos.com</div>
            <div>🌐 www.necttos.com</div>
            <div><small>#CIN: U72900KL2021OPC067869</small></div>
          </div>
        </footer>
      </div>
    );
  };

  return (
    <Main title="Circular" width="100%" height="100%">
      <Header>
        <div className="flex justify-start">
          <Button type="save" onClick={() => setShowForm(true)}>
            Create New
          </Button>
           <Input2
              type="text"
              fieldName="Search"
              state={search}
              setState={setSearch}
              className='w-[250px] h-[40px] border-0'
            />
            </div>
            <div>
          <Button type="close" onClick={onClose}>
            Close
          </Button>
        </div>
      </Header>

      <Body>
        {showForm ? (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-75 z-50 flex justify-center items-center">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md max-w-[21cm] w-full max-h-[29.7cm] h-full p-6 overflow-auto" style={{ boxSizing: 'border-box' }}>
              <div className="grid grid-cols-2 gap-6 mb-6">
                <Input2
                  fieldName="Reference No"
                  type="text"
                  name="refNo"
                  value={formData.refNo}
                  readOnly={true}
                  onChange={handleChange}
                  required
                  inputProps={{ readOnly: true }}
                />
                <Input2
                  fieldName="Date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
              
                />
                <Input2
                  fieldName="To"
                  type="text"
                  name="to"
                  value={formData.to}
                  onChange={handleChange}
                  required
                />
                <Input2
                  fieldName="Subject"
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
                <div>
                  <label htmlFor="type" className="block mb-2 font-semibold text-gray-700">Type</label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded p-2"
                  >
                    <option value="academic">Academic</option>
                    <option value="co-curricular">Co-curricular</option>
                    <option value="general">General</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block mb-2 font-semibold text-gray-700">Body</label>
                <RichTextEditor value={formData.body} onChange={handleBodyChange} />
              </div>
              <div className="mb-6">
              <Input2
                fieldName="CC"
                type="text"
                name="cc"
                value={formData.cc}
                onChange={handleChange}
                className="w-full"
                placeholder="Enter CC recipients"
              />
              </div>

              <div className="flex justify-end gap-4">
                <Button type="close" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit">Submit</Button>
              </div>
            </form>
          </div>
        ) : (
          <div ref={ref} className="overflow-auto max-w-6xl mx-auto mt-6">
            <Table width="100%" innerWidth={['10%', '20%', '20%', '50%']}>
              <Thead>
                <Tr>
                  <Th position={0}>Ref No</Th>
                  <Th position={1} onClick={() => requestSort('date')} icon={getIcon('date')}>
                    Date
                  </Th>
                  <Th position={2} onClick={() => requestSort('to')} icon={getIcon('to')}>
                    To
                  </Th>
                  <Th position={3} onClick={() => requestSort('subject')} icon={getIcon('subject')}>
                    Subject
                  </Th>
                </Tr>
              </Thead>
              <Tbody height="auto">
                {tableItems.map((circular, i) => (
                  <Tr key={i}>
                    <Td index={i} position={0}>
                      {circular.refNo}
                    </Td>
                    <Td index={i} position={1}>
                      {new Date(circular.date).toLocaleDateString()}
                    </Td>
                    <Td index={i} position={2}>
                      {circular.to}
                    </Td>
                    <Td index={i} position={3}>
                      {circular.subject}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </Body>
    </Main>
  );
};

export default Circular;
